import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {markInAppNavigation, storeWebSession} from './web-session.util';

/**
 * JS-1396 — the single post-login sequence.
 *
 * /login, passkey/login-verify and passkey/login-recovery all return the same
 * payload (jobrocksapi LoginController::buildLoginResponse()), and every one
 * of the three front screens that receives it has to do the same four things:
 * bail out to the password-reset flow if the account is on a temp password,
 * write `currentUser`, apply the account language, then route by role and
 * permission.
 *
 * It used to be three hand-copied versions of that sequence. The copies drifted
 * twice: once on the temp-password redirect, and once on the permission ladder
 * (the passkey screens never learned about `agency_client_profile`, so a user
 * whose only right is "Manage Client Settings" reached /project after 2FA — a
 * route AccessGuard refuses — while an ordinary login sent them to the right
 * page). One copy is the only durable defence, so all three screens call
 * completePostLogin() and nothing re-implements any part of it.
 */

/**
 * Where a `has_temp_password` response should send the user. The two login
 * paths genuinely differ here and the difference is not a bug:
 *
 * - 'password-reset': /login's own success handler. Its response carries a
 *   `token`, so it can deep-link straight into /password-reset/:token.
 * - 'login': the two passkey screens. buildLoginResponse() carries
 *   has_temp_password on that path but never a `token` (only /login's own
 *   temp-password branch, which runs before the 2FA gate, ever mints one), so
 *   /password-reset/:token would be a dead URL. No session has been written at
 *   that point either way, so /login is the safe, self-healing destination:
 *   re-entering the temp password there hits the real temp-password branch and
 *   gets a working reset link.
 */
export type TempPasswordDestination = 'password-reset' | 'login';

/**
 * JS-1396 follow-up, browser-pass fix. changeLang() below reloads the whole
 * page (window.location.href = ...) to re-bootstrap translations, which
 * destroys Angular router state - including the {nextUrl} an
 * overrideDestination navigation carries. When a reload is about to happen
 * on an override path, nextUrl is stashed here first so the override screen
 * (passkey-add-device.component.ts) can recover it after the reload; it
 * clears the key once read, so a later, unrelated login can never inherit a
 * stale destination.
 */
export const POST_LOGIN_NEXT_URL_SESSION_KEY = 'js1396PostLoginNextUrl';

export interface PostLoginOptions {
  router: Router;
  translate: TranslateService;
  tempPasswordDestination: TempPasswordDestination;
  /**
   * JS-1396 follow-up (recovery-code -> add-a-passkey prompt). When set, the
   * sequence below runs completely unchanged — temp-password bail-out first,
   * then the `currentUser` write, then the language switch — and only the
   * final navigation is redirected here instead of straight to the resolved
   * destination. The resolved destination travels with it, in router state
   * as `{nextUrl: resolvePostLoginUrl(response)}`, so the override screen can
   * still get the user to where they were always going.
   *
   * This only works because the caller (passkey-recovery.component.ts) is
   * reached with a session already written by the time the override screen
   * runs its own authenticated register-options call — deferring the
   * session write itself, not just the navigation, would break that.
   *
   * Browser-pass fix: when the account has a language set, the language
   * switch below reloads the page BEFORE this function's own navigate()
   * ever runs, which would otherwise destroy the router state the {nextUrl}
   * above depends on and strand the reload on the screen the user was
   * leaving. See POST_LOGIN_NEXT_URL_SESSION_KEY - nextUrl is stashed in
   * sessionStorage as a fallback for exactly that case, and the reload
   * itself is retargeted at `overrideDestination` rather than `router.url`.
   * When `overrideDestination` is unset the reload targets the resolved
   * destination itself.
   */
  overrideDestination?: string;
}

/**
 * The permission ladder, in /login's original order. First match wins.
 *
 * `client_dashboard` and `client_work_validation` are currently dead — nothing
 * in buildLoginResponse() emits either key — but they are kept so this helper
 * stays a faithful superset of what /login did, and so re-enabling them
 * server-side needs no front change.
 *
 * Exported separately from completePostLogin() so it can be unit-tested
 * without a Router.
 */
export function resolvePostLoginUrl(response: any): string {
  const permissions = (response && response.permissions) || {};
  const user = (response && response.user) || {};

  if (user.role === 'employee') {
    return '/profile';
  }
  if (permissions.projects) {
    return '/project';
  }
  if (permissions.employees) {
    return '/employee';
  }
  if (permissions.client_dashboard) {
    return '/client-dashboard';
  }
  if (permissions.agency_profile) {
    return '/agency/profile/' + user.agency_id;
  }
  if (permissions.agency_client_profile) {
    return '/agency-client/profile/' + user.agency_id;
  }
  if (permissions.client_work_validation) {
    return '/client-work-validation/dashboard';
  }

  // Nothing matched. The passkey screens already fell back to /project; /login
  // instead navigated with an unassigned `returnUrl` (i.e. `undefined`), which
  // is not a destination at all. /project is the better of the two and is now
  // what all three do.
  return '/project';
}

/**
 * Runs the whole post-login sequence. Callers do nothing else with the
 * response.
 */
export function completePostLogin(response: any, options: PostLoginOptions): void {
  const {router, translate, tempPasswordDestination, overrideDestination} = options;

  // First, before any session is written: an account on a temp password must
  // not end up with a half-written session it cannot use. This wins over
  // overrideDestination too - there is no route to an add-passkey prompt for
  // a session that was never written.
  if (response.has_temp_password) {
    if (tempPasswordDestination === 'password-reset') {
      router.navigate(['/password-reset/' + response.token]);
    } else {
      router.navigate(['/login']);
    }
    return;
  }

  const nextUrl = resolvePostLoginUrl(response);
  const destination = overrideDestination || nextUrl;

  localStorage.setItem('currentUser', JSON.stringify(response.user));

  // JS-1396 idle lock: gated logins carry a per-device session token.
  storeWebSession(response);

  if (response.user && response.user.language) {
    if (overrideDestination) {
      // Browser-pass fix: changeLang()'s reload below would otherwise land
      // back on router.url (still /passkey-recovery at this point) and
      // destroy the {nextUrl} the navigate() below would have carried,
      // sending every user with a language set straight past this screen
      // and into whatever the guards do with a bare reload. Stash nextUrl
      // so it survives the reload; sessionStorage can throw in some
      // private-browsing modes, and a failed write here must not break
      // login - it just means the override screen falls back to /project,
      // the same as a refresh with no state at all.
      try {
        sessionStorage.setItem(POST_LOGIN_NEXT_URL_SESSION_KEY, nextUrl);
      } catch (e) {
        // See comment above - degrade to the override screen's own /project
        // fallback rather than let a storage failure break the login.
      }
      changeLang(response.user.language, router, translate, destination);
    } else {
      // Bleona c. 59311: reloading to router.url (still the passkey screen
      // the user is leaving) repainted that screen with no translations
      // loaded for a moment before the guards moved on - an empty passkey
      // box flashing before the dashboard. Reload straight to where the
      // user is going; this also keeps resolvePostLoginUrl()'s destination,
      // which the old router.url reload threw away.
      changeLang(response.user.language, router, translate, nextUrl);
    }
  }

  if (overrideDestination) {
    router.navigate([overrideDestination], {state: {nextUrl}});
    return;
  }

  router.navigate([nextUrl]);
}

/**
 * Lifted unchanged from login.component.ts. A language switch reloads the page
 * (to re-bootstrap translations), so it has to carry the destination URL
 * through the reload itself.
 *
 * `target`: where the reload lands. completePostLogin() always passes the
 * destination, since router.url at reload time is still the screen the user
 * is leaving, not where they're headed. Omitted, it falls back to router.url.
 */
function changeLang(lang: string, router: Router, translate: TranslateService, target?: string): void {
  translate.setDefaultLang(lang);
  localStorage.setItem('Language', lang);
  const url = window.location.href;
  const arr = url.split('/');
  // JS-2070: or the new document takes this reload for a browser open and
  // asks for the passkey a second time, right after passkey-verify.
  markInAppNavigation();
  window.location.href = arr[0] + '//' + arr[2] + (target !== undefined ? target : router.url);
}
